import { DateTime } from 'luxon';

import {
  type SuppliersPageVendorPromotionListQuery,
  type SuppliersPageVendorPromotionRecordFragment,
  type SuppliersPageVendorPromotionTierFragment,
} from '../../../../../../../generated';
import {
  type PromotionCondition,
  type PromotionListResult,
  type PromotionOffer,
  type PromotionRow,
  type PromotionStatus,
  type PromotionTier,
  type PromotionTranslation,
} from '../types';

/** En dash, used wherever a value is absent. */
export const ABSENT_VALUE = '–';

const DEFAULT_ERROR_MESSAGE = 'Failed to load promotions.';

type PromotionListResponse =
  SuppliersPageVendorPromotionListQuery['vendorPromotionList'];

type PromotionTierNode = SuppliersPageVendorPromotionTierFragment;
type PromotionOfferNode = PromotionTierNode['offers'][number];
type PromotionConditionNode = PromotionTierNode['conditions'][number];

interface PromotionMoney {
  amount: number;
  currency: string;
  decimalPlaces: number;
}

// `VendorPromotion.beginAt` and `endAt` are epoch milliseconds although the
// scalar is AWSTimestamp; the buyer apps read milliseconds too. UTC keeps the
// distributor's calendar day.
export const formatPromotionTimestamp = (
  milliseconds: number | null | undefined
): string => {
  if (milliseconds === null || milliseconds === undefined) {
    return ABSENT_VALUE;
  }

  const date = DateTime.fromMillis(milliseconds, { zone: 'utc' });

  return date.isValid ? date.toFormat('dd MMM yyyy') : ABSENT_VALUE;
};

/** Formats a minor-unit `MoneyV2`, e.g. `$12.34`. */
export const formatPromotionMoney = (
  money: PromotionMoney | null | undefined
): string => {
  if (!money) {
    return ABSENT_VALUE;
  }

  const value = money.amount / 10 ** money.decimalPlaces;

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency,
      minimumFractionDigits: money.decimalPlaces,
      maximumFractionDigits: money.decimalPlaces,
    }).format(value);
  } catch {
    return `${value.toFixed(money.decimalPlaces)} ${money.currency}`;
  }
};

/**
 * `Decimal` arrives as `{ amount: string }`. The string is kept as sent so a
 * fractional quantity such as `0.5` is not rounded on the way to the screen.
 */
export const formatPromotionDecimal = (
  decimal: { amount: string } | null | undefined
): string => decimal?.amount.trim() || ABSENT_VALUE;

/** Formats a `Decimal` percentage, e.g. `10%`. */
export const formatPromotionPercentage = (
  decimal: { amount: string } | null | undefined
): string => {
  const amount = formatPromotionDecimal(decimal);

  return amount === ABSENT_VALUE ? ABSENT_VALUE : `${amount}%`;
};

export const formatTierMode = (tierMode: string): string =>
  tierMode === 'STACKABLE' ? 'Stackable' : 'Exclusive';

const STATUS_LABELS: Record<string, PromotionStatus> = {
  DISABLED: { label: 'Disabled', tone: 'default' },
  SCHEDULED: { label: 'Scheduled', tone: 'primary' },
  LIVE: { label: 'Live', tone: 'success' },
  EXPIRED: { label: 'Expired', tone: 'warning' },
};

/**
 * The backend evaluates the status from the switch and the date window. An
 * unrecognised value shows as itself, so a status added later still reads.
 */
export const mapPromotionStatus = (status: string): PromotionStatus =>
  STATUS_LABELS[status] ?? { label: status, tone: 'default' };

export const formatScope = (
  isAppliedToAllChats: boolean,
  chatCount: number
): string => {
  if (isAppliedToAllChats) {
    return 'All chats';
  }

  return `${chatCount} ${chatCount === 1 ? 'chat' : 'chats'}`;
};

/** Turns one offer union member into a readable label and amount. */
export const mapPromotionOffer = (
  offer: PromotionOfferNode
): PromotionOffer => {
  switch (offer.__typename) {
    case 'VendorPromotionFixedPriceOffer':
      return {
        typeLabel: 'Fixed price',
        value: formatPromotionMoney(offer.price),
      };
    case 'VendorPromotionReducedPriceOffer':
      return {
        typeLabel: 'Price reduction',
        value: formatPromotionMoney(offer.priceReduction),
      };
    case 'VendorPromotionCartReducedPriceOffer':
      return {
        typeLabel: 'Cart price reduction',
        value: formatPromotionMoney(offer.priceReduction),
      };
    case 'VendorPromotionDiscountPercentageOffer':
      return {
        typeLabel: 'Discount',
        value: formatPromotionPercentage(offer.discountPercentage),
      };
    case 'VendorPromotionCartDiscountPercentageOffer':
      return {
        typeLabel: 'Cart discount',
        value: formatPromotionPercentage(offer.discountPercentage),
      };
    case 'VendorPromotionCartFreeExtraUnitsOffer':
      return {
        typeLabel: 'Free extra units',
        value: formatPromotionDecimal(offer.quantityOffered),
      };
    case 'VendorPromotionFreeUnitsIncludedOffer':
      return {
        typeLabel: 'Free units included',
        value: formatPromotionDecimal(offer.quantityOffered),
      };
    default:
      return { typeLabel: 'Offer', value: ABSENT_VALUE };
  }
};

const mapCondition = (
  condition: PromotionConditionNode
): PromotionCondition => {
  const isAmount = condition.metric === 'AMOUNT';

  return {
    metricLabel: isAmount ? 'Amount' : 'Quantity',
    minimum: isAmount
      ? formatPromotionMoney(condition.minimumAmount)
      : formatPromotionDecimal(condition.minimumQuantity),
    maximum: isAmount
      ? formatPromotionMoney(condition.maximumAmount)
      : formatPromotionDecimal(condition.maximumQuantity),
  };
};

const mapTier = (tier: PromotionTierNode): PromotionTier => ({
  id: tier.id,
  name: tier.name,
  maxApplications: tier.maxApplications ?? null,
  conditions: tier.conditions.map(mapCondition),
  offers: tier.offers.map(mapPromotionOffer),
});

const mapTranslation = (
  translation: SuppliersPageVendorPromotionRecordFragment['translations'][number]
): PromotionTranslation => ({
  locale: translation.locale,
  name: translation.name,
  description: translation.description,
  shortDescription: translation.shortDescription ?? null,
  displayName: translation.displayName ?? null,
  displayDescription: translation.displayDescription ?? null,
});

/** Maps one record into the row the table and the modal render. */
export const mapPromotionRecord = (
  record: SuppliersPageVendorPromotionRecordFragment
): PromotionRow => {
  const tiers = record.promotion.tiers ?? [];

  return {
    id: record.id,
    // The schema stopped promising a code, so a record without one still reads.
    code: record.code || ABSENT_VALUE,
    name: record.promotion.name,
    isEnabled: record.isActive,
    status: mapPromotionStatus(record.status),
    beginsAt: formatPromotionTimestamp(record.promotion.beginAt),
    endsAt: formatPromotionTimestamp(record.promotion.endAt),
    tierModeLabel: formatTierMode(record.promotion.tierMode),
    tierCount: tiers.length,
    scopeLabel: formatScope(record.isAppliedToAllChats, record.chats.length),
    categoryCode: record.categoryCode || ABSENT_VALUE,
    shortDescription: record.promotion.shortDescription ?? null,
    description: record.promotion.description ?? null,
    translations: (record.translations ?? []).map(mapTranslation),
    tiers: tiers.map(mapTier),
  };
};

/**
 * Maps the `VendorPromotionListResponse` union into a display result. Returns
 * `null` while there is no response yet; the failure members become
 * `{ kind: 'error' }`.
 */
export const mapVendorPromotionResponse = (
  response: PromotionListResponse | null | undefined
): PromotionListResult | null => {
  if (!response) {
    return null;
  }

  switch (response.__typename) {
    case 'VendorPromotionListResult':
      return {
        kind: 'success',
        page: {
          promotions: (response.edges ?? []).map((edge) =>
            mapPromotionRecord(edge.node)
          ),
          pageInfo: {
            hasNextPage: response.pageInfo?.hasNextPage ?? false,
            endCursor: response.pageInfo?.endCursor ?? '',
          },
        },
      };
    case 'InputValidationFailure':
    case 'AuthorizationFailure':
    case 'NotFoundError':
      return {
        kind: 'error',
        message: response.message ?? DEFAULT_ERROR_MESSAGE,
      };
    default:
      return { kind: 'error', message: DEFAULT_ERROR_MESSAGE };
  }
};
